import React, { useState, useEffect } from 'react';
import { PerformanceLogger } from '../utils/performanceLogger';
import './PerformanceDebugPanel.css';

const PerformanceDebugPanel: React.FC = () => {
  const [showPanel, setShowPanel] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  
  useEffect(() => {
    // Check for debug mode in URL params
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') {
      setShowPanel(true);
      loadLogs();
    }
  }, []);
  
  const loadLogs = () => {
    const perfLogger = PerformanceLogger.getInstance();
    const storedLogs = perfLogger.getStoredLogs();
    setLogs(storedLogs);
  };
  
  const exportCSV = () => {
    const perfLogger = PerformanceLogger.getInstance();
    const csv = perfLogger.exportLogsAsCSV();
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `voice-coach-performance-${new Date().toISOString()}.csv`;
    a.click();
  };
  
  const clearLogs = () => {
    const perfLogger = PerformanceLogger.getInstance();
    perfLogger.clearLogs();
    setLogs([]);
  };
  
  if (!showPanel) return null;
  
  const avgWaitTime = logs.length > 0
    ? (logs.reduce((sum, log) => sum + log.analysisWaitTime, 0) / logs.length).toFixed(2)
    : 0;
  
  const errorRate = logs.length > 0
    ? ((logs.filter(log => log.errorOccurred).length / logs.length) * 100).toFixed(1)
    : 0;
  
  return (
    <div className="performance-debug-panel">
      <div className="debug-header">
        <h3>🔧 Performance Debug Panel</h3>
        <button onClick={() => setShowPanel(false)} className="close-btn">×</button>
      </div>
      
      <div className="debug-stats">
        <div className="stat">
          <span className="stat-label">Total Logs:</span>
          <span className="stat-value">{logs.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Avg Wait Time:</span>
          <span className="stat-value">{avgWaitTime}s</span>
        </div>
        <div className="stat">
          <span className="stat-label">Error Rate:</span>
          <span className="stat-value">{errorRate}%</span>
        </div>
      </div>
      
      <div className="debug-actions">
        <button onClick={loadLogs}>Refresh</button>
        <button onClick={exportCSV}>Export CSV</button>
        <button onClick={clearLogs}>Clear Logs</button>
      </div>
      
      <div className="debug-logs">
        <h4>Recent Performance Logs</h4>
        <div className="log-table">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Duration</th>
                <th>Wait</th>
                <th>Audio</th>
                <th>Phoneme</th>
                <th>Transcript</th>
                <th>Device</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.slice(-10).reverse().map((log, index) => (
                <tr key={index} className={log.errorOccurred ? 'error-row' : ''}>
                  <td>{new Date(log.timestamp).toLocaleTimeString()}</td>
                  <td>{log.recordingDuration.toFixed(1)}s</td>
                  <td className="highlight">{log.analysisWaitTime.toFixed(1)}s</td>
                  <td>{log.analysisStages.audioProcessing.toFixed(2)}s</td>
                  <td>{log.analysisStages.phonemeAnalysis.toFixed(2)}s</td>
                  <td>{log.analysisStages.transcription.toFixed(2)}s</td>
                  <td>{log.deviceType}</td>
                  <td>{log.errorOccurred ? '❌' : '✅'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="debug-footer">
        <p>Add ?debug=true to URL to show this panel</p>
      </div>
    </div>
  );
};

export default PerformanceDebugPanel;